/**
 *  @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Icon } from 'semantic-ui-react';
import { omit } from 'lodash';
import uuid from 'uuid';

import { Modal, Button } from '../../../components';
import { ProductForm } from './form';
import { prepareFloatForServer } from '../../../lib/numberFormatter';
import { GET_PRODUCTS_QUERY } from '../containers/ProductsList';

const ADD_PRODUCT_QUERY = gql`
    mutation addProduct($input: AddProductInput!) {
        addProduct(input: $input) {
            product {
                id
                name
                code
                price
                supplierPrice
                quantity
                manufacturer {
                    id
                    name
                }
                type
            }
        }
    }
`;

const intlMessages = defineMessages({
    nameExistingsError: {
        id: 'pages.inventory.AddProductDialog.nameExistingsError',
        defaultMessage: 'Product with the same name already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

function AddProductDialog({ trigger, open, onClose, intl }) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const searchQuery = useSelector(({ inventory }) => inventory.get('searchQuery'));
    const manufacturersFilter = useSelector(({ inventory }) => inventory.get('manufacturersFilter'));

    const methods = useForm({
        defaultValues: {
            name: '',
            price: '',
            quantity: '',
            supplierPrice: '',
            code: '',
        },
    });
    const { handleSubmit, reset, setError, formState: { isSubmitting, isDirty } } = methods;

    const [addProduct] = useMutation(ADD_PRODUCT_QUERY, {
        update: (store, { data: { addProduct: result } }) => {
            try {
                const data = store.readQuery({
                    query: GET_PRODUCTS_QUERY,
                    variables: { salon, searchQuery, manufacturersFilter, first: 500 },
                });
                if (data?.viewer?.salon?.products?.edges) {
                    const newProduct = {
                        node: result.product,
                        __typename: 'ClientEdge',
                        cursor: btoa(result.product.id),
                    };
                    store.writeQuery({
                        query: GET_PRODUCTS_QUERY,
                        variables: { salon, searchQuery, manufacturersFilter, first: 500 },
                        data: {
                            ...data,
                            viewer: {
                                ...data.viewer,
                                salon: {
                                    ...data.viewer.salon,
                                    products: {
                                        ...data.viewer.salon.products,
                                        edges: [newProduct, ...data.viewer.salon.products.edges],
                                    },
                                },
                            },
                        },
                    });
                }
            } catch (e) {
                // cache not populated — safe to ignore
            }
        },
    });

    const onSubmit = async formData => {
        const quantity = parseInt(formData.quantity);
        const price = prepareFloatForServer(formData.price);
        const supplierPrice = formData.supplierPrice
            ? prepareFloatForServer(formData.supplierPrice)
            : undefined;

        try {
            await addProduct({
                variables: {
                    input: { ...omit(formData, ['quantity']), quantity, price, supplierPrice, salon },
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    addProduct: {
                        __typename: 'AddProductPayload',
                        product: {
                            __typename: 'Product',
                            id: uuid.v4(),
                            manufacturer: { __typename: 'Manufacturer', id: formData.manufacturer, name: '' },
                            code: formData.code || null,
                            supplierPrice: formData.supplierPrice || null,
                            ...omit(formData, ['manufacturer']),
                        },
                    },
                },
            });
            reset();
            onClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                if (graphQLError.name === 'AlreadyExists' && graphQLError.data?.error === 'NAME_ALREADY_EXISTS') {
                    setError('name', { message: intl.formatMessage(intlMessages.nameExistingsError) });
                    return;
                }
                setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
                return;
            }
            setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal trigger={trigger} onClose={onClose} open={open}>
                <Modal.Header>
                    <FormattedMessage
                        id="pages.inventory.AddProductDialog.header"
                        defaultMessage="New Product"
                    />
                </Modal.Header>
                <Modal.Content>
                    <ProductForm />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.inventory.AddProductDialog.add"
                            defaultMessage="Add"
                        />
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.inventory.AddProductDialog.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
}

export default injectIntl(AddProductDialog);
